import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Mail, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        ...form,
        subject: form.subject || "New enquiry",
      });
      toast.success("Message sent — thank you. Su will be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative z-10 bg-onyx text-alabaster py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-16">
        <div className="md:col-span-6">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-mono text-xs tracking-[0.3em] uppercase text-ochre-light"
          >
            06 — Contact
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-none mt-6"
          >
            Let&apos;s <span className="italic text-ochre-light">talk.</span>
          </motion.h2>
          <p className="font-sans text-lg leading-relaxed text-alabaster/70 mt-8 max-w-md">
            For development, financing or collaboration enquiries, reach out below or
            connect directly.
          </p>

          <div className="mt-12 space-y-6">
            <a
              href="mailto:su.dharmapala@gmail.com"
              data-testid="contact-email-link"
              className="flex items-center gap-4 group"
            >
              <Mail className="w-5 h-5 text-ochre-light" strokeWidth={1.5} />
              <span className="font-sans text-lg link-underline">su.dharmapala@gmail.com</span>
            </a>
            <a
              href="https://instagram.com/onyxandochre"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-link"
              className="flex items-center gap-4 group"
            >
              <Instagram className="w-5 h-5 text-ochre-light" strokeWidth={1.5} />
              <span className="font-sans text-lg link-underline">@onyxandochre</span>
            </a>
            <div className="flex items-start gap-4 pt-4 border-t border-alabaster/15">
              <User className="w-5 h-5 text-ochre-light mt-1" strokeWidth={1.5} />
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-alabaster/50">
                  Representation
                </div>
                <div className="font-sans text-lg mt-1">Anjanette Fennel</div>
                <div className="font-sans text-sm text-alabaster/60">Agent — enquiries welcome</div>
              </div>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="md:col-span-6 space-y-8 md:pt-16"
        >
          <input
            className="editorial-input"
            placeholder="Your name"
            data-testid="contact-input-name"
            value={form.name}
            onChange={set("name")}
          />
          <input
            className="editorial-input"
            type="email"
            placeholder="Your email"
            data-testid="contact-input-email"
            value={form.email}
            onChange={set("email")}
          />
          <input
            className="editorial-input"
            placeholder="Subject"
            data-testid="contact-input-subject"
            value={form.subject}
            onChange={set("subject")}
          />
          <textarea
            className="editorial-input resize-none"
            rows={4}
            placeholder="Tell me about your project"
            data-testid="contact-input-message"
            value={form.message}
            onChange={set("message")}
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-btn"
            className="font-sans uppercase text-xs tracking-[0.2em] bg-ochre text-onyx px-10 py-4 hover:bg-ochre-light transition-colors duration-300 disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
